import { type Array as YArray, type Map as YMap, transact } from 'yjs';

import {
  earliestTrashDate,
  groupDocMetaEntries,
  isTrashedEntry,
} from './doc-meta-entries';

/**
 * Collapses page-list entries that share a doc id into the first one, and
 * returns how many entries were removed.
 *
 * The array order is the CRDT-converged order, so every client that repairs
 * the same list keeps the same entry and deletes the same items: concurrent
 * repairs converge. If any removed entry was trashed, the kept entry becomes
 * trashed with the earliest trash date, so a doc the user trashed stays
 * trashed. Nothing is written when there is no duplicate.
 */
export function dedupeDocMetas(pages: YArray<YMap<unknown>>): number {
  const doc = pages.doc;
  const duplicated = [...groupDocMetaEntries(pages).values()].filter(
    entries => entries.length > 1
  );
  if (!doc || duplicated.length === 0) {
    return 0;
  }

  const removed = new Set<YMap<unknown>>();
  transact(doc, () => {
    for (const [kept, ...later] of duplicated) {
      if (!isTrashedEntry(kept) && later.some(isTrashedEntry)) {
        kept.set('trash', true);
        const trashDate = earliestTrashDate(later);
        if (trashDate !== undefined) {
          kept.set('trashDate', trashDate);
        }
      }
      later.forEach(entry => removed.add(entry));
    }
    const items = pages.toArray();
    for (let index = items.length - 1; index >= 0; index--) {
      if (removed.has(items[index])) {
        pages.delete(index, 1);
      }
    }
  });
  return removed.size;
}
